import { Component, Input, OnInit } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { FormBuilder, FormGroup, Validators, FormArray, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms'
import { AlertService, InspectionsService } from '@app/core/services'
import { Scopes } from '@app/utils/scopes'

@Component({
    selector: 'modal-changeInspector',
    templateUrl: 'modal-changeInspector.component.html',
    styleUrls: ['../../../app.component.scss']
})
export class ModalChangeInspector implements OnInit {
    // Inputs
    @Input() public inspectors = {}
    @Input() public inspectionId
    @Input() public code
    
    // Loaders
    loading = false
    
    // FormGroup values
    form: FormGroup
    numberOfInspectors = 2
    
    // Data API
    data = []
    scopeOptions: { name: string; value: string; selected: boolean }[] = []
    scopeOptionsView: { name: string; value: string }[] = []

    constructor(
        private formBuilder: FormBuilder,
        public modal: NgbActiveModal,
        private alertService: AlertService,
        private inspectionsService: InspectionsService,
        private scopes: Scopes,
    ) {}

    ngOnInit() {
        this.form = this.formBuilder.group({
            onlyOneInspector: [true, Validators.required],
            inspectors: this.formBuilder.array(
                [],
                [this.uniqueInspectorsAndScopesValidator()]
            )
        })

        this.updateInspectors()

        this.loadDefaultValues()

        this.form.get('onlyOneInspector')?.valueChanges.subscribe(() => {
            this.updateInspectors()

            this.getFormArrayInspectors.updateValueAndValidity()
        })

        this.getInspectors()
    }

    get getFormArrayInspectors(): FormArray {
        return this.form.get('inspectors') as FormArray
    }

    getInspectorControl(i: number) {
        return this.getFormArrayInspectors.at(i).get('inspector')
    }

    getScopesControl(i: number) {
        return this.getFormArrayInspectors.at(i).get('scopesInspector')
    }

    private createInspectorGroup(): FormGroup {
        return this.formBuilder.group({
            inspector: [null, Validators.required],
            scopesInspector: [[]]
        })
    }

    private updateInspectors(): void {
        const onlyOneInspector = this.form.get('onlyOneInspector')?.value

        const targetCount = onlyOneInspector ? 1 : this.numberOfInspectors

        while (this.getFormArrayInspectors.length < targetCount) {
            const group = this.createInspectorGroup()

            if (!onlyOneInspector) {
                group.get('scopesInspector')?.setValidators([Validators.required])
            } else {
                group.get('scopesInspector')?.clearValidators()
            }
            
            group.get('scopesInspector')?.updateValueAndValidity()

            this.getFormArrayInspectors.push(group)
        }

        while (this.getFormArrayInspectors.length > targetCount) {
            this.getFormArrayInspectors.removeAt(this.getFormArrayInspectors.length - 1)
        }

        this.getFormArrayInspectors.updateValueAndValidity()

        this.getFormArrayInspectors.controls.forEach(control => {
            control.markAsTouched();
            control.updateValueAndValidity();
        })

        this.getFormArrayInspectors.updateValueAndValidity({ emitEvent: false })
    }

    private uniqueInspectorsAndScopesValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const array = control as FormArray

            const onlyOneInspector = array.parent?.get('onlyOneInspector')?.value

            const inspectorMap = new Map<number, number[]>()
            const scopeMap = new Map<string, number[]>()

            array.controls.forEach((group, index) => {
                const inspector = group.get('inspector')?.value
                const scopes = group.get('scopesInspector')?.value || []

                // Duplicate inspectors
                if (inspector) {
                    if (!inspectorMap.has(inspector)) inspectorMap.set(inspector, [])
                    
                    inspectorMap.get(inspector)!.push(index)
                }

                // Scopes only if apply
                if (!onlyOneInspector) {
                    scopes.forEach((s: string) => {
                        if (!scopeMap.has(s)) scopeMap.set(s, [])
                            
                        scopeMap.get(s)!.push(index);
                    })
                }
            })

            let hasError = false

            inspectorMap.forEach(indexes => {
                if (indexes.length > 1) hasError = true
            })

            scopeMap.forEach(indexes => {
                if (indexes.length > 1) hasError = true
            })

            return hasError ? { duplicatedAssignments: true } : null
        }
    }

    getInspectors() {
        this.inspectionsService.getInspectors(this.inspectionId)
            .subscribe(
                response => {
                    this.data = response.data

                    for(let scope of response.data.scopes) {
                        const scopeData = this.scopes.getScopeById(scope)

                        const scopeOption = {
                            name: scopeData.scopeName,
                            value: scopeData.scopeId,
                            selected: false
                        }

                        this.scopeOptions.push(scopeOption)
                    }

                    this.scopeOptionsView = this.scopeOptions
                },
                error => {
                    this.alertService.error(error, { autoClose: true })
                })
    }

    isInspectorSelected(inspectorId: number, currentIndex: number): boolean {
        return this.getFormArrayInspectors.controls.some((group, index) => {

            if (index === currentIndex) {
                return false
            }

            return group.get('inspector')?.value === inspectorId
        })
    }

    addedScope(event: { name: string; value: string; selected: boolean }) {
        const index = this.scopeOptions.findIndex((scope) => scope.value === event.value)

        if(index !== -1) {
            this.scopeOptions[index].selected = true

            this.scopeOptionsView = this.scopeOptions
                .map((scope) => {
                    if(scope.selected) return null

                    return {
                        name: scope.name,
                        value: scope.value
                    }
                })
                .filter((scope) => scope !== null)
        }
    }

    removedScope(event: { value: { name: string; value: string; selected: boolean } }) {
        const index = this.scopeOptions.findIndex((scope) => scope.value === event.value.value)

        if(index !== -1) {
            this.scopeOptions[index].selected = false

            this.scopeOptionsView = this.scopeOptions
                .map((scope) => {
                    if(scope.selected) return null

                    return {
                        name: scope.name,
                        value: scope.value
                    }
                })
                .filter((scope) => scope !== null)
        }
    }

    private loadDefaultValues(): void {
        const inspectorsData = Object.values(this.inspectors || {}) as any[]

        const onlyOneInspector = inspectorsData.length === 1

        this.form.get('onlyOneInspector')?.setValue(onlyOneInspector)

        this.updateInspectors()

        inspectorsData.forEach((item, index) => {
            const group = this.getFormArrayInspectors.at(index)

            if (!group) return

            group.patchValue({
                inspector: item.id,
                scopesInspector: item.scopes || []
            })
        })

        this.getFormArrayInspectors.updateValueAndValidity()
    }

    onSubmit() {
        this.loading = true

        // Reset alerts on submit
        this.alertService.clear();

        // Stop here if form is invalid
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            this.getFormArrayInspectors.controls.forEach(group => {
                group.get('inspector')?.markAsTouched()
                group.get('scopesInspector')?.markAsTouched()

                group.get('inspector')?.updateValueAndValidity()
                group.get('scopesInspector')?.updateValueAndValidity()
            })

            this.getFormArrayInspectors.updateValueAndValidity()

            this.loading = false

            this.alertService.error('Hay campos sin llenar', { autoClose: true })
            return
        }

        const onlyOneInspector = this.form.get('onlyOneInspector')?.value
        if(!onlyOneInspector) {
            if (this.hasDuplicateInspectors()) {
                this.loading = false

                this.alertService.error('No se puede asignar el mismo inspector más de una vez', { autoClose: true })
                return
            }

            if (this.hasDuplicateScopes()) {
                this.loading = false

                this.alertService.error('No se puede asignar el mismo alcance a mas de un inspector', { autoClose: true })
                return
            }

            if (!this.areAllScopesAssigned()) {
                this.loading = false

                this.alertService.error('Todos los alcances deben estar asignados', { autoClose: true })
                return
            }
        }

        const payload = {
            isOnlyOneInspector: onlyOneInspector,
            inspectors: this.getFormArrayInspectors.value.map(item => ({
                inspectorId: item.inspector,
                scopes: onlyOneInspector ? [] : (item.scopesInspector || [])
            }))
        }

        this.inspectionsService.changeInspector(payload, this.inspectionId)
            .subscribe(
                response => {
                    this.loading = false
                    this.alertService.clear()
                    this.alertService.success('Se han actualizado los inspectores correctamente', { autoClose: true })
                    this.modal.close(true)
                    this.modal.dismiss()
                },
                error => {
                    this.alertService.error(error, { autoClose: true })
                    this.loading = false
                }
            )
    }

    private hasDuplicateInspectors(): boolean {
        const inspectors = this.getFormArrayInspectors.value
            .map(item => item.inspector)
            .filter(Boolean)

        return new Set(inspectors).size !== inspectors.length
    }

    private hasDuplicateScopes(): boolean {
        const scopes = this.getFormArrayInspectors.value
            .flatMap(item => item.scopesInspector || [])

        return new Set(scopes).size !== scopes.length
    }

    private areAllScopesAssigned(): boolean {
        const expectedScopes = new Set(this.scopeOptions.map(scope => scope.value))

        const assignedScopes = new Set(
            this.getFormArrayInspectors.value
                .flatMap(item => item.scopesInspector || [])
        )

        if (expectedScopes.size !== assignedScopes.size) return false

        return [...expectedScopes].every(scope => assignedScopes.has(scope))
    }
}